import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Document, Page, pdfjs } from "react-pdf";
import IconButton from '@material-ui/core/IconButton';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import ClearIcon from '@material-ui/icons/Clear';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import ReactLoading from "react-loading";
import "../Styles/GenPdf.scss";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const GenPdf = ({ 
  isOpen, 
  file, 
  closeDoc, 
  pageNumber: propPageNumber, 
  scale: propScale = 0.8,
  onTextLayerReady
}) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.8);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [textLayerRendered, setTextLayerRendered] = useState(false);
  const [initialScaleSet, setInitialScaleSet] = useState(false);

  const pdfViewerRef = useRef(null);
  const pdfContentRef = useRef(null);

  const nextPage = useCallback(() => {
    if (pageNumber !== numPages) {
      setPageNumber(prev => prev + 1);
    }
  }, [pageNumber, numPages]);

  const prevPage = useCallback(() => {
    if (pageNumber !== 1) {
      setPageNumber(prev => prev - 1);
    }
  }, [pageNumber]);

  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.2, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const verifyPageNumber = useCallback(() => {
    if (propPageNumber !== undefined) {
      return Math.min(propPageNumber, numPages || 1);
    }
    return Math.min(pageNumber || 1, numPages || 1);
  }, [propPageNumber, pageNumber, numPages]);

  const handleClose = useCallback(() => {
    setPageNumber(1);
    setScale(0.8);
    setIsFullscreen(false);
    setTextLayerRendered(false);
    setInitialScaleSet(false); // NOUVEAU: reset du flag
    closeDoc();
  }, [closeDoc]);

  const calculateFitScale = useCallback((pageWidth, pageHeight) => {
    if (!pdfViewerRef.current) return 0.8;

    const container = pdfViewerRef.current.querySelector('.pdf-content');
    if (!container) return 0.8;

    const containerWidth = container.clientWidth - 40;
    const containerHeight = container.clientHeight - 40;

    const scaleX = containerWidth / pageWidth;
    const scaleY = containerHeight / pageHeight;

    return Math.min(scaleX, scaleY, 2);
  }, []);

  const onPageLoadSuccess = useCallback((page) => {
    if (!initialScaleSet && propScale === 0.8) {
      const { width, height } = page.getViewport({ scale: 1 });
      const fitScale = calculateFitScale(width, height);
      setScale(fitScale);
      setInitialScaleSet(true);
    } else if (!initialScaleSet && propScale !== 0.8) {
      setScale(propScale);
      setInitialScaleSet(true);
    }
  }, [calculateFitScale, propScale, initialScaleSet]);

  const onTextLayerRenderSuccess = useCallback(() => {
    setTextLayerRendered(true);
    
    setTimeout(() => {
      if (onTextLayerReady && typeof onTextLayerReady === 'function') {
        onTextLayerReady();
      }
    }, 500);
  }, [onTextLayerReady]);

  const handleWheel = useCallback((event) => {
    if (!isOpen) return;

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();

      const zoomStep = 0.1;
      const delta = event.deltaY;

      if (delta < 0) {
        setScale(prev => Math.min(prev + zoomStep, 3));
      } else {
        setScale(prev => Math.max(prev - zoomStep, 0.5));
      }
    }
  }, [isOpen]);

  const handleKeyPress = useCallback((event) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'ArrowLeft':
        prevPage();
        break;
      case 'ArrowRight':
        nextPage();
        break;
      case 'Escape':
        handleClose();
        break;
      case '+':
        zoomIn();
        break;
      case '-':
        zoomOut();
        break;
      default:
        break;
    }
  }, [isOpen, prevPage, nextPage, handleClose, zoomIn, zoomOut]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    const pdfContent = pdfContentRef.current;

    if (pdfContent && isOpen) {
      pdfContent.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        pdfContent.removeEventListener('wheel', handleWheel);
      };
    }
  }, [handleWheel, isOpen]);

  useEffect(() => {
    if (isFullscreen && pdfViewerRef.current) {
      pdfViewerRef.current.style.zIndex = '999999';
      document.body.style.overflow = 'hidden';
    } else if (pdfViewerRef.current) {
      pdfViewerRef.current.style.zIndex = '10000';
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isFullscreen]);

  useEffect(() => {
    setTextLayerRendered(false);
    setInitialScaleSet(false);
  }, [file]);

  if (!isOpen) return null;

  return (
    <div
      className={`pdf-viewer-overlay ${isFullscreen ? 'fullscreen' : ''}`}
      ref={pdfViewerRef}
    >
      <div className="pdf-viewer-container">
        <div className="pdf-header">
          <div className="pdf-title">
            Aperçu PDF
          </div>
          <div className="pdf-controls">
            {numPages > 1 && (
              <>
                <IconButton
                  className="control-btn"
                  onClick={prevPage}
                  disabled={pageNumber === 1}
                >
                  <ChevronLeft />
                </IconButton>
                <span className="page-indicator">
                  {pageNumber} / {numPages}
                </span>
                <IconButton
                  className="control-btn"
                  onClick={nextPage}
                  disabled={pageNumber === numPages}
                >
                  <ChevronRight />
                </IconButton>
                <div className="divider"></div>
              </>
            )}

            <IconButton className="control-btn" onClick={zoomOut}>
              <ZoomOutIcon />
            </IconButton>
            <span className="zoom-indicator">
              {Math.round(scale * 100)}%
            </span>
            <IconButton className="control-btn" onClick={zoomIn}>
              <ZoomInIcon />
            </IconButton>

            <div className="divider"></div>

            <IconButton className="control-btn" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>

            <IconButton className="close-btn" onClick={handleClose}>
              <ClearIcon />
            </IconButton>
          </div>
        </div>

        <div className="pdf-content" ref={pdfContentRef}>
          <Document
            file={file}
            onLoadSuccess={(pdf) => setNumPages(pdf.numPages)}
            onLoadError={console.error}
            loading={
              <div className="pdf-loading">
                <ReactLoading type={"spinningBubbles"} color="#f97316" />
                <p>Chargement du PDF...</p>
              </div>
            }
            noData={
              <div className="pdf-no-data">
                <p>Aucun fichier PDF à afficher</p>
              </div>
            }
          >
            <div className="pdf-page-container">
              <Page
                pageNumber={verifyPageNumber()}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={false}
                onLoadSuccess={onPageLoadSuccess}
                onTextLayerRenderSuccess={onTextLayerRenderSuccess}
              />
            </div>
          </Document>
        </div>

        {numPages >= 5 && (
          <div className="pdf-footer">
            <div className="quick-nav">
              <span className="quick-nav-label">Navigation rapide :</span>
              <button
                className="quick-nav-btn"
                onClick={() => setPageNumber(1)}
              >
                Début
              </button>
              <button
                className="quick-nav-btn"
                onClick={() => setPageNumber(Math.ceil(numPages / 2))}
              >
                Milieu
              </button>
              <button
                className="quick-nav-btn"
                onClick={() => setPageNumber(numPages)}
              >
                Fin
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
};

export default GenPdf;